import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import { useFeatures } from '@atc/features';
import { formatCurrency, formatNumber } from '@atc/string-fns';
import { calculatePersonalPayment } from '@atc/wallet-payment-fns';

import { Button, ColoredBackground, Text } from 'reaxl';
import { InfoIcon, KBBDealIndicator, Pricing } from 'reaxl-molecules';

import { defaultPaymentInfo } from 'axl-config';

import {
    vdpResultsDuck,
} from '@/ducks/vdp';

import usePaymentData from '@/hooks/usePaymentData';

function PricingCard({
    includeTaxesFees,
    handlePriceAndPaymentsClick = () => { },
    showDealIndicator = false,
    includeFinancePaymentOfferToWalletCTA = false,
}) {
    const {
        kbb_fair_price_details: [isKbbFppDetailsEnabled],
    } = useFeatures([
        'kbb_fair_price_details',
    ]);

    const ref = useRef();
    const containerRef = useRef();

    const { pricingDetail = {} } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const [isLoading, hasError, paymentsData] = usePaymentData();
    const [financeOffersObj, setFinanceOffersObj] = useState({});

    useEffect(() => {
        if (paymentsData) {
            paymentsData.map((item) => {
                if (item.paymentLabel === 'Finance') {
                    setFinanceOffersObj(item);
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(paymentsData)]);
    const {
        dealIndicator,
        kbbFppDelta = 0,
    } = pricingDetail;

    const showKbbFppDetails = isKbbFppDetailsEnabled && dealIndicator && kbbFppDelta > 0;

    const financePayment = useMemo(() => {
        const { payment, paymentInfo, customRate } = financeOffersObj;
        if (!customRate) {
            return payment;
        }

        return Math.max(0, calculatePersonalPayment(paymentInfo.pricingDetails.salePrice, paymentInfo || defaultPaymentInfo, customRate)) || 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(financeOffersObj)]);

    const walletCTA = (includeFinancePaymentOfferToWalletCTA && !isLoading && !hasError && financePayment > 0) ? (
        <div className="margin-left-3 text-normal">
            <div className="display-flex flex-wrap text-size-200">
                <span className="margin-right-1">
                    <Text
                        size={200}
                        weight="bold"
                    >
                        {formatCurrency(financePayment)}
                    </Text>
                    /mo.
                    {includeTaxesFees && <span>*</span>}
                </span>
                <Button
                    bsStyle="text"
                    onClick={handlePriceAndPaymentsClick}
                >
                    See finance details
                </Button>
            </div>
            {includeTaxesFees && (
                <Text
                    size={100}
                    color="gray-dark"
                    componentClass="div"
                >
                    * includes est. taxes and fees
                </Text>
            )}
        </div>
    ) : (
        <Button
            bsStyle="text"
            className="display-block margin-left-3 text-size-200 text-normal"
            onClick={handlePriceAndPaymentsClick}
        >
            See price and payment details
        </Button>
    );

    const handleOverlayTrigger = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    const dealIndicatorWithFppDetails = (
        <div className="display-flex gap-3">
            <KBBDealIndicator
                pricingDetail={pricingDetail}
                flat
                logoSize="small"
            />
            {showKbbFppDetails && (
                <div
                    ref={containerRef}
                    className="display-flex align-items-center text-size-200"
                >
                    {formatNumber({
                        value: kbbFppDelta,
                        style: 'currency',
                    })}
                    {' '}
                    below market&nbsp;
                    <InfoIcon
                        onClick={handleOverlayTrigger}
                        placement="top"
                        textColor="text-gray-base"
                        className="text-size-400"
                        popoverId="fairMarketPriceInfo"
                        ref={ref}
                        container={containerRef.current}
                        trigger={['click']}
                        infoText={(
                            <Text>
                                This vehicle is listed
                                {' '}
                                {formatNumber({
                                    value: kbbFppDelta,
                                    style: 'currency',
                                })}
                                {' '}
                                below the Kelley Blue Book
                                <sup>&reg;</sup>
                                &nbsp;Fair Purchase Price
                            </Text>
                        )}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div
            className={clsx({
                'margin-vertical-3 margin-top-sm-0': includeFinancePaymentOfferToWalletCTA && !showDealIndicator,
                'margin-vertical-4 margin-top-sm-0': !includeFinancePaymentOfferToWalletCTA && !showDealIndicator,
                'margin-bottom-3': includeFinancePaymentOfferToWalletCTA && showDealIndicator,
                'margin-bottom-4': !includeFinancePaymentOfferToWalletCTA && showDealIndicator,
            })}
        >
            <Pricing
                uiContext={showDealIndicator ? 'withDealIndicator' : 'withPayment'}
                showSavings
                data={pricingDetail}
                walletCTA={walletCTA}
            />
            {(showDealIndicator && dealIndicator) && dealIndicatorWithFppDetails}
            {!showDealIndicator && showKbbFppDetails && (
                <ColoredBackground
                    className={clsx('padding-2 line-height-sm text-size-200', { 'margin-top-2': includeFinancePaymentOfferToWalletCTA })}
                    data-cmp="kbbFairPriceDetails"
                    uiContext="success"
                >
                    <Text weight="bold">
                        {formatNumber({
                            value: kbbFppDelta,
                            style: 'currency',
                        })}
                    </Text>
                    <Text color="subdued">
                        &nbsp;Below Kelley Blue Book
                        <sup>&reg;</sup>
                        &nbsp;Fair Purchase Price
                    </Text>
                </ColoredBackground>
            )}
        </div>
    );

}

PricingCard.propTypes = {
    includeTaxesFees: PropTypes.bool,
    /**
     * Handler for price and payments button click
     */
    handlePriceAndPaymentsClick: PropTypes.func,
    /**
     * Toggle KBB deal indicator display
     */
    showDealIndicator: PropTypes.bool,
    /**
     * Include Finance Payment Offer to Wallet CTA
     */
    includeFinancePaymentOfferToWalletCTA: PropTypes.bool,
};

export default PricingCard;
